import React, { useEffect, useMemo, useState } from 'react';
import Layout, { EmptyState, Pager, Spinner } from '../components/Layout';
import Modal from '../components/Modal';
import { CardBadges, EditTypeChip, ReceptionStamp } from '../components/StatusBadge';
import { PrescriptionUploader } from '../components/Prescription';
import CaseNotes from '../components/CaseNotes';
import ShareLinks from '../components/ShareLinks';
import { useCaseAction, useCases } from '../hooks/useCases';
import { cases as casesApi, errorMessage } from '../utils/api';
import { SELECTABLE_EDIT_TYPES } from '../utils/constants';
import { formatDateTime, formatNumber, isolate } from '../utils/format';
import { toast } from 'react-toastify';

const TABS = [
  { id: 'delivery', label: 'آماده تحویل', status: 'report_completed' },
  { id: 'active', label: 'در جریان', status: 'reception_completed,ready_for_reporting,in_progress' },
  { id: 'closed', label: 'تحویل‌شده', status: 'closed' },
];

export default function ReceptionDashboard() {
  const [tab, setTab] = useState('delivery');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const activeTab = TABS.find((t) => t.id === tab);
  const params = useMemo(
    () => ({ status: activeTab.status, ...(search ? { q: search } : {}) }),
    [activeTab.status, search]
  );

  const { cases, count, page, setPage, pageCount, pageSize, loading, error, reload } =
    useCases(params);
  const { run, pendingId } = useCaseAction(reload);

  return (
    <Layout
      title="میز پذیرش"
      subtitle="ثبت بیمار جدید، تماس با بیمار و تحویل گزارش"
      tabs={TABS.map((t) => ({ ...t, count: t.id === tab ? count : undefined }))}
      activeTab={tab}
      onTabChange={setTab}
      actions={
        <button type="button" className="btn btn--primary" onClick={() => setShowForm(true)}>
          ＋ ثبت بیمار جدید
        </button>
      }
    >
      <div className="toolbar">
        <input
          type="search"
          className="input input--search"
          placeholder="جست‌وجوی نام بیمار، پزشک یا شماره نسخه…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      {loading && <Spinner />}
      {error && <p className="alert alert--danger">{error}</p>}

      {!loading && !error && cases.length === 0 && (
        <EmptyState
          title={tab === 'delivery' ? 'گزارش آماده‌ی تحویلی وجود ندارد.' : 'پرونده‌ای در این فهرست نیست.'}
          hint={tab === 'delivery' ? 'به‌محض تکمیل گزارش توسط ادیتور، اینجا نمایش داده می‌شود.' : undefined}
        />
      )}

      <div className="cards">
        {cases.map((item) => (
          <article key={item.id} className={`card ${item.is_urgent ? 'card--urgent' : ''}`}>
            <header className="card__head">
              <div>
                <h3 className="card__title"><bdi>{item.name}</bdi></h3>
                <p className="card__meta">
                  <bdi>{item.doctor}</bdi> · نسخه <span className="num">{item.order_number}</span>
                </p>
              </div>
              <CardBadges item={item} />
            </header>

            <div className="card__row">
              <EditTypeChip code={item.edit_type_code} name={item.edit_type_name} />
              <ReceptionStamp at={item.reception_time} />
            </div>

            {item.status !== 'closed' && (
              <div className="card__row card__row--tools">
                {/* پزشک گاهی بعد از پذیرش زنگ می‌زند و می‌گوید این یکی فوری است */}
                <button
                  type="button"
                  className={`urgent-toggle ${item.is_urgent ? 'is-on' : ''}`}
                  disabled={pendingId === item.id}
                  onClick={() =>
                    run(
                      item.id,
                      () => casesApi.setUrgent(item.id, !item.is_urgent),
                      item.is_urgent ? 'فوریت برداشته شد.' : 'پرونده فوری شد.'
                    )
                  }
                >
                  ⚡ {item.is_urgent ? 'فوری است — برداشتن' : 'علامت‌گذاری فوری'}
                </button>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => setEditing(item)}
                >
                  ✎ اصلاح اطلاعات
                </button>
              </div>
            )}

            <PrescriptionUploader
              caseId={item.id}
              items={item.prescriptions}
              onChanged={reload}
              disabled={item.status === 'closed'}
            />

            {/* پذیرش لینک را برای پزشک و بیمار می‌فرستد و اگر پیامک
                نرسید همان را دوباره کپی می‌کند. */}
            <ShareLinks caseId={item.id} />

            <CaseNotes caseId={item.id} notes={item.notes} onChanged={reload} />

            <dl className="card__facts">
              <div>
                <dt>ادیتور</dt>
                <dd><bdi>{item.editor_name}</bdi></dd>
              </div>
              <div>
                <dt>تماس با بیمار</dt>
                <dd>{item.call_time ? formatDateTime(item.call_time) : '—'}</dd>
              </div>
              <div>
                <dt>تحویل</dt>
                <dd>{item.delivery_time ? formatDateTime(item.delivery_time) : '—'}</dd>
              </div>
            </dl>

            {item.status === 'report_completed' && (
              <footer className="card__foot">
                <button
                  type="button"
                  className="btn btn--ghost"
                  disabled={pendingId === item.id}
                  onClick={() =>
                    run(item.id, () => casesApi.call(item.id), 'زمان تماس ثبت شد.')
                  }
                >
                  📞 {item.call_time ? 'ثبت تماس مجدد' : 'ثبت تماس با بیمار'}
                </button>
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={pendingId === item.id}
                  onClick={() =>
                    run(item.id, () => casesApi.deliver(item.id), 'گزارش تحویل بیمار شد.')
                  }
                >
                  ✅ تحویل گزارش
                </button>
              </footer>
            )}
          </article>
        ))}
      </div>

      {!loading && cases.length > 0 && pageCount === 1 && (
        <p className="list-footnote">{formatNumber(count)} پرونده در این فهرست</p>
      )}

      <Pager
        page={page}
        pageCount={pageCount}
        pageSize={pageSize}
        count={count}
        onChange={setPage}
      />

      <NewCaseModal open={showForm} onClose={() => setShowForm(false)} onCreated={reload} />
      <EditCaseModal
        item={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          reload();
        }}
      />
    </Layout>
  );
}

/**
 * اصلاح اطلاعاتِ اشتباهِ ثبت‌شده.
 *
 * نام، پزشک و شماره‌ی نسخه همیشه (تا پیش از تحویل) قابل اصلاح‌اند. نوع پرونده
 * و فوریت فقط تا پیش از اینکه ادیتوری پرونده را بردارد، چون بعد از آن مبنای
 * کارانه‌ی کسی هستند. سرور همین مرز را دوباره بررسی می‌کند؛ این‌جا فقط برای
 * آن است که کاربر دکمه‌ای را نبیند که نتیجه‌اش خطاست.
 */
function EditCaseModal({ item, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setErrors({});
    setForm(
      item
        ? {
            name: item.name,
            doctor: item.doctor,
            order_number: item.order_number,
            patient_phone: item.patient_phone || '',
            edit_type: item.edit_type,
          }
        : null
    );
  }, [item]);

  if (!item || !form) return null;

  const earlyEnough = ['reception_completed', 'ready_for_reporting'].includes(item.status);
  const update = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setErrors({});

    // فقط آنچه واقعاً عوض شده فرستاده می‌شود — وگرنه فرستادن نوع پرونده‌ی
    // دست‌نخورده روی پرونده‌ای که ادیتور برداشته، بی‌دلیل خطا می‌گیرد.
    const payload = {};
    ['name', 'doctor', 'order_number', 'patient_phone'].forEach((key) => {
      if (form[key] !== item[key]) payload[key] = form[key];
    });
    if (earlyEnough && Number(form.edit_type) !== item.edit_type) {
      payload.edit_type = Number(form.edit_type);
    }

    if (Object.keys(payload).length === 0) {
      toast.info('چیزی تغییر نکرده است.');
      setBusy(false);
      return;
    }

    try {
      await casesApi.edit(item.id, payload);
      toast.success('اطلاعات پرونده اصلاح شد.');
      onSaved();
    } catch (err) {
      const data = err?.response?.data;
      if (data && typeof data === 'object' && !data.detail) setErrors(data);
      toast.error(errorMessage(err, 'اصلاح انجام نشد.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      title={`اصلاح اطلاعات «${isolate(item.name)}»`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            انصراف
          </button>
          <button type="submit" form="rec-edit-case" className="btn btn--primary" disabled={busy}>
            {busy ? 'در حال ذخیره…' : 'ذخیره'}
          </button>
        </>
      }
    >
      <form id="rec-edit-case" className="form" onSubmit={submit}>
        <div className="field">
          <label htmlFor="r-name">نام بیمار</label>
          <input id="r-name" value={form.name} onChange={update('name')} required />
          {errors.name && <span className="field__error">{errors.name[0]}</span>}
        </div>

        <div className="field">
          <label htmlFor="r-doctor">پزشک ارجاع‌دهنده</label>
          <input id="r-doctor" value={form.doctor} onChange={update('doctor')} required />
          {errors.doctor && <span className="field__error">{errors.doctor[0]}</span>}
        </div>

        <div className="field">
          <label htmlFor="r-order">شماره نسخه</label>
          <input
            id="r-order"
            className="num"
            value={form.order_number}
            onChange={update('order_number')}
            required
          />
          {errors.order_number && <span className="field__error">{errors.order_number[0]}</span>}
        </div>

        <div className="field">
          <label htmlFor="r-phone">موبایل بیمار</label>
          <input
            id="r-phone"
            className="num"
            type="tel"
            dir="ltr"
            inputMode="numeric"
            placeholder="09123456789"
            value={form.patient_phone}
            onChange={update('patient_phone')}
          />
          <span className="field__hint">
            برای فرستادن لینک تصاویر با پیامک. اگر شماره‌ی درستی نیست خالی بگذارید —
            شماره‌ی نامعتبر ذخیره نمی‌شود.
          </span>
        </div>

        <div className="field">
          <label htmlFor="r-type">نوع پرونده</label>
          <select
            id="r-type"
            value={form.edit_type}
            onChange={update('edit_type')}
            disabled={!earlyEnough}
          >
            {SELECTABLE_EDIT_TYPES.map((type) => (
              <option key={type.id} value={type.id}>{type.name}</option>
            ))}
            {/* پرونده‌ی قدیمی ممکن است نوعی داشته باشد که دیگر انتخابی نیست */}
            {!SELECTABLE_EDIT_TYPES.some((t) => t.id === item.edit_type) && (
              <option value={item.edit_type}><bdi>{item.edit_type_name}</bdi></option>
            )}
          </select>
          {!earlyEnough && (
            <span className="field__hint">
              کار ادیتور روی این پرونده شروع شده، پس نوع پرونده از اینجا عوض
              نمی‌شود — مبنای کارانه است. در صورت نیاز با مدیر سامانه تماس بگیرید.
            </span>
          )}
        </div>

        <p className="form__note">
          هر اصلاح با مقدار قبلی و بعدی، نام شما و ساعت دقیق در دفتر رخدادها
          ثبت می‌شود. زمان‌ها، وضعیت و کارانه از اینجا تغییر نمی‌کنند.
        </p>
      </form>
    </Modal>
  );
}

function NewCaseModal({ open, onClose, onCreated }) {
  const empty = {
    name: '',
    doctor: '',
    order_number: '',
    patient_phone: '',
    // شناسه‌ها از پرتو می‌آیند و پشت‌سرهم نیستند؛ عدد ثابت ننویسید.
    edit_type: SELECTABLE_EDIT_TYPES[0].id,
    is_urgent: false,
    note: '',
  };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const update = (key) => (event) =>
    setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setFieldErrors({});
    try {
      await casesApi.create({
        ...form,
        edit_type: Number(form.edit_type),
        note: form.note.trim(),
      });
      toast.success(
        form.is_urgent
          ? 'بیمار فوری ثبت شد و به تصویربرداری اطلاع داده شد.'
          : 'بیمار ثبت شد و به تصویربرداری اطلاع داده شد.'
      );
      setForm(empty);
      onCreated();
      onClose();
    } catch (error) {
      const data = error?.response?.data;
      if (data && typeof data === 'object' && !data.detail) setFieldErrors(data);
      toast.error(errorMessage(error, 'ثبت بیمار انجام نشد.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      title="ثبت بیمار جدید"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            انصراف
          </button>
          <button type="submit" form="new-case" className="btn btn--primary" disabled={busy}>
            {busy ? 'در حال ثبت…' : 'ثبت در سامانه'}
          </button>
        </>
      }
    >
      <form id="new-case" onSubmit={submit} className="form">
        <div className="field">
          <label htmlFor="case-name">نام بیمار</label>
          <input id="case-name" value={form.name} onChange={update('name')} required />
          {fieldErrors.name && <span className="field__error">{fieldErrors.name[0]}</span>}
        </div>

        <div className="field">
          <label htmlFor="case-doctor">پزشک ارجاع‌دهنده</label>
          <input id="case-doctor" value={form.doctor} onChange={update('doctor')} required />
          {fieldErrors.doctor && <span className="field__error">{fieldErrors.doctor[0]}</span>}
        </div>

        <div className="field">
          <label htmlFor="case-order">شماره نسخه</label>
          <input
            id="case-order"
            className="num"
            value={form.order_number}
            onChange={update('order_number')}
            required
          />
          {fieldErrors.order_number && (
            <span className="field__error">{fieldErrors.order_number[0]}</span>
          )}
        </div>

        <div className="field">
          <label htmlFor="case-phone">موبایل بیمار</label>
          <input
            id="case-phone"
            className="num"
            type="tel"
            dir="ltr"
            inputMode="numeric"
            placeholder="09123456789"
            value={form.patient_phone}
            onChange={update('patient_phone')}
          />
          <span className="field__hint">
            برای فرستادن لینک تصاویر با پیامک. اگر شماره‌ی درستی نیست خالی بگذارید —
            شماره‌ی نامعتبر ذخیره نمی‌شود.
          </span>
        </div>

        <div className="field">
          <label htmlFor="case-type">نوع ادیت</label>
          <select id="case-type" value={form.edit_type} onChange={update('edit_type')}>
            {SELECTABLE_EDIT_TYPES.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>

        <label className={`check ${form.is_urgent ? 'is-checked' : ''}`}>
          <input
            type="checkbox"
            checked={form.is_urgent}
            onChange={(event) =>
              setForm((prev) => ({ ...prev, is_urgent: event.target.checked }))
            }
          />
          <span>
            ⚡ فوری — به درخواست پزشک خارج از نوبت رسیدگی شود
            <br />
            <small className="field__hint">
              پرونده بالای فهرست همه‌ی نقش‌ها می‌نشیند و با نوار قرمز مشخص می‌شود.
            </small>
          </span>
        </label>

        <div className="field">
          <label htmlFor="case-note">یادداشت (اختیاری)</label>
          <textarea
            id="case-note"
            rows={3}
            maxLength={4000}
            value={form.note}
            onChange={update('note')}
            placeholder="مثلاً: بیمار حساسیت دارویی دارد، یا تأکید خاص پزشک…"
          />
          <span className="field__hint">
            سرِ رشته‌ی یادداشت‌های پرونده. تصویربرداری، ادیتور و مسئول فنی آن را
            می‌بینند و می‌توانند چیزی به آن اضافه کنند.
          </span>
        </div>

        <p className="form__note">
          ساعت پذیرش و نام کاربر پذیرش‌کننده به‌صورت خودکار ثبت می‌شود.
        </p>
      </form>
    </Modal>
  );
}
